import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import { formatRelativeTime } from "@/v2-backoffice-client/uptime/uptime.utils"

// Structural target shape -- only the mute-relevant fields. Keeps the
// badge usable from any fragment that includes IsMuted (full
// `UptimeTargetFragment`, the slim `UptimeInlineFragment`, etc.)
// without forcing callers to up-fetch. `MutedAt` is typed `any` here
// because gql-codegen lands all date columns on the schema as `any`
// for the generated fragments, and we need to accept whichever shape
// the caller's fragment lands on.
export type UptimeMuteBadgeTarget = {
  IsMuted: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  MutedAt?: any | null
  MutedByName?: string | null
  MutedReason?: string | null
}

export type UptimeMuteBadgeProps = Omit<ComponentProps<"span">, "title"> & {
  target: UptimeMuteBadgeTarget
  size?: "sm" | "md"
}

// UptimeMuteBadge is the small "muted" pill rendered next to a target
// name wherever it appears (board row, view sheet header). It carries
// the full mute context in a tooltip so an operator scanning the
// board can answer "why is this silenced and who did it?" without
// having to open the detail sheet.
function UptimeMuteBadge(props: UptimeMuteBadgeProps) {
  const { target, size = "sm", className, ...spanProps } = props

  if (!target.IsMuted) return null

  const tooltip = [
    target.MutedAt ? `Muted ${formatRelativeTime(target.MutedAt)}` : "Muted",
    target.MutedByName ? `by ${target.MutedByName}` : null,
    target.MutedReason ? `— ${target.MutedReason}` : null,
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <span
      className={cn(
        "uptime-mute-badge",
        size === "md" && "uptime-mute-badge-md",
        className,
      )}
      title={tooltip}
      {...spanProps}
    >
      <Icon src="bell-slash" size={size === "md" ? 12 : 10} />
      muted
    </span>
  )
}

export default UptimeMuteBadge
